import { Injectable } from '@angular/core';
import {
    CanActivate,
    Router,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { AuthService } from '../auth.service';
import { isAdminRole, extractUserRole } from '../role.constants';

@Injectable({ providedIn: 'root' })
export class RoleGuard implements CanActivate {
    constructor(
        private router: Router,
        private authService: AuthService
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree {
        const user = this.authService.getCurrentUser();

        if (!user) {
            return this.router.createUrlTree(['/sign-in']);
        }

        const userRole = extractUserRole(user?.role || user);
        const userPermissions: string[] = (user.permissions || []).map((p: any) =>
            typeof p === 'string' ? p.toLowerCase() : (p?.name || '').toLowerCase()
        );

        const allowedRoles = (route.data?.['roles'] as string[] | undefined)?.map(r => r.toLowerCase());
        const requiredPermissions = (
            (route.data?.['permissions'] as string[] | undefined) ||
            (route.data?.['permission'] ? [route.data?.['permission']] : [])
        ).map(p => p.toLowerCase());

        // 1. If route requires permissions, check if user has at least one matching permission
        if (requiredPermissions.length > 0) {
            const hasPerm = requiredPermissions.some((p) => userPermissions.includes(p));
            if (hasPerm) {
                return true;
            }
        }

        // 2. If user role is directly in allowedRoles (for routes without specific permissions, e.g. /dashboard)
        if (allowedRoles && allowedRoles.length > 0) {
            const hasRole = allowedRoles.includes(userRole);
            if (hasRole && requiredPermissions.length === 0) {
                return true;
            }
        }

        // 3. Fallback for administrator if route has no specific permissions
        if (userRole === 'administrator' && requiredPermissions.length === 0) {
            return true;
        }

        // 4. Access Denied
        console.warn(
            `[RoleGuard] Access denied for user "${user.username}" (role: ${userRole}). Required roles:`,
            allowedRoles,
            'Required permissions:',
            requiredPermissions
        );
        const fallback = isAdminRole(userRole) ? '/dashboard' : '/user/home';
        return this.router.createUrlTree([fallback]);
    }
}
